import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Car, Users, Fuel, Settings, ArrowLeft, Check, Calendar as CalendarIcon, AlertTriangle, ChevronRight, Clock, Upload, FileCheck, X, ChevronLeft, Image, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate time options (30 minute intervals, 24/7)
const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      options.push(`${hour}:${minute}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();


const formatLocalDateYYYYMMDD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const CarDetailsPage = () => {
  const { t, language } = useLanguage();
  const { carId } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickupDate, setPickupDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [pickupTime, setPickupTime] = useState("10:00");
  const [returnTime, setReturnTime] = useState("10:00");
  const [bookingForm, setBookingForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    message: ""
  });
  const [totalDays, setTotalDays] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [dateConflict, setDateConflict] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [priceDetails, setPriceDetails] = useState(null);
  const [discounts, setDiscounts] = useState([]);


  // Insurance state (prices set per car in admin)
  const [insuranceType, setInsuranceType] = useState("TPL");
  const [insurancePricePerDay, setInsurancePricePerDay] = useState(0);

  // Reviews state

  useEffect(() => {
    if (!car) return;
    // Default insurance: TPL
    setInsuranceType("TPL");
    setInsurancePricePerDay(Number(car.tpl_price_per_day || 0));
  }, [car]);

  const [reviewsData, setReviewsData] = useState({ reviews: [], total_reviews: 0, average_rating: 0 });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customer_name: "",
    customer_email: "",
    rating: 0,
    comment: ""
  });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  
  // Document upload states
  const [idDocument, setIdDocument] = useState(null);
  const [licenseDocument, setLicenseDocument] = useState(null);
  const [idUploading, setIdUploading] = useState(false);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const idInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  
  // Gallery state
  const [showGallery, setShowGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Check if same-day booking
  const isSameDay = pickupDate && returnDate && pickupDate === returnDate;
  
  // Get all images (main + gallery)
  const allImages = car ? [car.image_url, ...(car.gallery_images || [])] : [];

  useEffect(() => {
    fetchCarDetails();
    fetchBookedDates();
    fetchDiscounts();
    fetchReviews();
  }, [carId]);

  useEffect(() => {
    if (pickupDate && returnDate && car) {
      const pickup = new Date(pickupDate);
      const returnD = new Date(returnDate);
      let days = Math.ceil((returnD - pickup) / (1000 * 60 * 60 * 24));
      
      // Same-day booking counts as 1 day
      if (days === 0) {
        days = 1;
      }
      
      if (days > 0) {
        setTotalDays(days);
        checkDateAvailability(pickupDate, returnDate);
        calculatePriceWithDiscount(days);
      } else {
        setTotalDays(0);
        setTotalPrice(0);
        setDateConflict(null);
        setPriceDetails(null);
      }
    }
  }, [pickupDate, returnDate, car, insurancePricePerDay]);

  const fetchDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/price-discounts`);
      setDiscounts(response.data.discounts || []);
    } catch (error) {
      console.error("Error fetching discounts:", error);
    }
  };

  const calculatePriceWithDiscount = async (days) => {
    try {
      const response = await axios.get(`${API}/calculate-price`, {
        params: { car_id: carId, days: days, pickup_date: pickupDate, return_date: returnDate }
      });
      setPriceDetails(response.data);

      const insuranceTotal = (Number(insurancePricePerDay) || 0) * (days || 0);
      setTotalPrice((response.data.final_price || 0) + insuranceTotal);
    } catch (error) {
      console.error("Error calculating price:", error);
      const insuranceTotal = (Number(insurancePricePerDay) || 0) * (days || 0);
      setTotalPrice((days * car.price_per_day) + insuranceTotal);
      setPriceDetails(null);
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await axios.get(`${API}/cars/${carId}/reviews`);
      setReviewsData({
        reviews: response.data.reviews || [],
        total_reviews: response.data.total_reviews || 0,
        average_rating: response.data.average_rating || 0
      });
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviewsData({ reviews: [], total_reviews: 0, average_rating: 0 });
    } finally {
      setReviewsLoading(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();

    if (!reviewForm.rating || reviewForm.rating < 1 || reviewForm.rating > 5) {
      toast.error(t('rating_required'));
      return;
    }

    if (!reviewForm.customer_name || !reviewForm.customer_email) {
      toast.error(t('fill_all_fields'));
      return;
    }

    setReviewSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        car_id: carId,
        customer_name: reviewForm.customer_name,
        customer_email: reviewForm.customer_email,
        rating: reviewForm.rating,
        comment: reviewForm.comment || ""
      });

      toast.success(t('review_success'));
      setReviewForm({ customer_name: "", customer_email: "", rating: 0, comment: "" });
      fetchReviews();
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(t('review_error'));
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (file, documentType) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('invalid_file_type'));
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('file_too_large'));
      return;
    }
    
    const setUploading = documentType === 'id' ? setIdUploading : setLicenseUploading;
    const setDocument = documentType === 'id' ? setIdDocument : setLicenseDocument;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      
      const response = await axios.post(`${API}/upload/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setDocument({
        url: response.data.url,
        filename: response.data.filename,
        originalName: file.name
      });
      
      toast.success(t('document_uploaded'));
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(t('document_upload_error'));
    } finally {
      setUploading(false);
    }
  };

  const fetchBookedDates = async () => {
    try {
      const response = await axios.get(`${API}/cars/${carId}/booked-dates`);
      setBookedDates(response.data.booked_dates || []);
    } catch (error) {
      console.error("Error fetching booked dates:", error);
    }
  };

  const checkDateAvailability = async (pickup, returnD) => {
    setCheckingAvailability(true);
    try {
      const response = await axios.get(`${API}/cars/${carId}/check-availability`, {
        params: { pickup_date: pickup, return_date: returnD }
      });
      if (!response.data.is_available) {
        setDateConflict(response.data.conflicting_bookings);
      } else {
        setDateConflict(null);
      }
    } catch (error) {
      console.error("Error checking availability:", error);
    }
    setCheckingAvailability(false);
  };

  const fetchCarDetails = async () => {
    try {
      const response = await axios.get(`${API}/cars/${carId}`);
      setCar(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching car details:", error);
      toast.error(t('car_load_error'));
      setLoading(false);
    }
  };

  const validateBookingForm = () => {
    if (!pickupDate || !returnDate) {
      toast.error(t('select_dates_error'));
      return false;
    }

    const pickup = new Date(pickupDate);
    const returnD = new Date(returnDate);

    // For different dates, return must be after pickup
    if (returnD < pickup) {
      toast.error(t('return_after_pickup'));
      return false;
    }

    // For same-day booking, validate return time is after pickup time
    if (isSameDay && returnTime <= pickupTime) {
      toast.error(t('return_time_after_pickup'));
      return false;
    }

    if (dateConflict && dateConflict.length > 0) {
      toast.error(t('dates_not_available'));
      return false;
    }

    if (!bookingForm.customer_name || !bookingForm.customer_email || !bookingForm.customer_phone) {
      toast.error(t('fill_all_fields'));
      return false;
    }

    // Enforce minimum rental days for this car
    const minDays = car?.min_rental_days || 1;
    if (totalDays > 0 && totalDays < minDays) {
      toast.error(`${t('min_days_error') || 'Kjo makinë kërkon minimumi'} ${minDays} ${t('days') || 'ditë'}`);
      return false;
    }
    
    // Check if documents are uploaded (required)
    if (!idDocument || !licenseDocument) {
      toast.error(t('documents_required_error'));
      return false;
    }

    return true;
  };

  const handleBookingWithoutPayment = async (e) => {
    e.preventDefault();
    
    if (!validateBookingForm()) return;

    try {
      const bookingData = {
        car_id: carId,
        customer_name: bookingForm.customer_name,
        customer_email: bookingForm.customer_email,
        customer_phone: bookingForm.customer_phone,
        pickup_date: pickupDate,
        return_date: returnDate,
        pickup_time: pickupTime,
        return_time: returnTime,
        total_price: totalPrice,
        language: language,
        insurance_type: insuranceType,
        insurance_price_per_day: insurancePricePerDay,
        message: bookingForm.message,
        id_document_url: idDocument?.url || "",
        license_document_url: licenseDocument?.url || ""
      };

      await axios.post(`${API}/bookings`, bookingData);
      toast.success(t('booking_success'));
      
      setBookingForm({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        message: ""
      });
      setPickupDate("");
      setReturnDate("");
      setPickupTime("10:00");
      setReturnTime("10:00");
      setDateConflict(null);
      setIdDocument(null);
      setLicenseDocument(null);
      
      fetchBookedDates();
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(t('booking_error'));
    }
  };

  const handlePayOnline = async (e) => {
    e.preventDefault();
    
    if (!validateBookingForm()) return;

    setPaymentLoading(true);

    try {
      const bookingData = {
        car_id: carId,
        customer_name: bookingForm.customer_name,
        customer_email: bookingForm.customer_email,
        customer_phone: bookingForm.customer_phone,
        pickup_date: pickupDate,
        return_date: returnDate,
        pickup_time: pickupTime,
        return_time: returnTime,
        total_price: totalPrice,
        language: language,
        insurance_type: insuranceType,
        insurance_price_per_day: insurancePricePerDay,
        message: bookingForm.message,
        id_document_url: idDocument?.url || "",
        license_document_url: licenseDocument?.url || ""
      };

      const bookingResponse = await axios.post(`${API}/bookings`, bookingData);
      const bookingId = bookingResponse.data.id;

      const origin = window.location.origin;
      const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
        booking_id: bookingId,
        origin_url: origin
      });

      window.location.href = paymentResponse.data.url;
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error(t('payment_error'));
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('car_not_found')}</h2>
          <Button onClick={() => navigate('/')}>{t('back_to_home')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Car className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Rent Car Kaci</span>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <Button 
                data-testid="back-to-home-button"
                onClick={() => navigate('/')} 
                variant="outline" 
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{t('back')}</span>
              </Button>
            </div>

          </div>
        </div>
      </nav>

      {/* Car Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Car Image Gallery and Details */}
          <div>
            {/* Main Image with Carousel */}
            <Card className="border-none shadow-xl overflow-hidden mb-2 group cursor-pointer" onClick={() => setShowImageModal(true)}>
              <div className="relative bg-zinc-500 h-96 flex items-center justify-center p-6">
                <img 
                  src={allImages[currentImageIndex] ? (allImages[currentImageIndex].startsWith('http') ? allImages[currentImageIndex] : `${BACKEND_URL}${allImages[currentImageIndex]}`) : car.image_url} 
                  alt={`${car.brand} ${car.model}`}
                  className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                  data-testid="car-main-image"
                />
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev > 0 ? prev - 1 : allImages.length - 1); }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev < allImages.length - 1 ? prev + 1 : 0); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
                      data-testid="gallery-next"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 rounded-full px-3 py-1">
                      <span className="text-white text-xs">{currentImageIndex + 1} / {allImages.length}</span>
                    </div>
                  </>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center pointer-events-none">
                  <div className="text-white luxury-text text-sm tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                    {t('click_to_zoom')}
                  </div>
                </div>
              </div>
            </Card>

            {/* Thumbnail Strip */}
            {allImages.length > 1 && (
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="gallery-thumbnails">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex ? 'border-yellow-500 ring-1 ring-yellow-500/50' : 'border-transparent hover:border-gray-400'
                    }`}
                  >
                    <img 
                      src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`} 
                      alt="" 
                      className="w-full h-full object-cover bg-gray-200" 
                    />
                  </button>
                ))}
              </div>
            )}

                {/* Reviews */}
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-bold text-lg">{t('reviews_title')}</h3>
                      <p className="text-sm text-gray-500">
                        {t('average_rating')}: <span className="font-semibold text-gray-800">{reviewsData.average_rating}</span>
                        <span className="text-gray-400"> • </span>
                        <span className="text-gray-600">{reviewsData.total_reviews} {t('reviews_count')}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-yellow-500">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < Math.round(reviewsData.average_rating || 0) ? 'fill-current' : ''}`} />
                      ))}
                    </div>
                  </div>

                  {reviewsLoading ? (
                    <p className="text-sm text-gray-500">{t('loading')}</p>
                  ) : reviewsData.reviews.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-gray-700 font-medium">{t('no_reviews')}</p>
                      <p className="text-gray-500 text-sm">{t('be_first_review')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reviewsData.reviews.map((r) => (
                        <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-gray-900">{r.customer_name}</p>
                              {r.is_verified && (
                                <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                  {t('verified_customer')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-yellow-500">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} className={`h-4 w-4 ${i < (r.rating || 0) ? 'fill-current' : ''}`} />
                              ))}
                            </div>
                          </div>
                          {r.comment && <p className="mt-3 text-gray-700 text-sm whitespace-pre-wrap">{r.comment}</p>}
                        </div>


                      ))}
                    </div>
                  )}

                  {/* Write review form */}
                  <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h4 className="font-semibold text-gray-900 mb-4">{t('write_review')}</h4>

                    <form onSubmit={submitReview} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-700">{t('full_name')}</Label>
                          <Input
                            value={reviewForm.customer_name}
                            onChange={(e) => setReviewForm({ ...reviewForm, customer_name: e.target.value })}
                            className="mt-2"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">{t('email')}</Label>
                          <Input
                            type="email"
                            value={reviewForm.customer_email}
                            onChange={(e) => setReviewForm({ ...reviewForm, customer_email: e.target.value })}
                            className="mt-2"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-700">{t('your_rating')}</Label>
                        <div className="mt-2 flex items-center gap-2">
                          {Array.from({ length: 5 }).map((_, i) => {
                            const val = i + 1;
                            return (
                              <button
                                key={val}
                                type="button"
                                onClick={() => setReviewForm({ ...reviewForm, rating: val })}
                                className={`p-1 rounded ${reviewForm.rating >= val ? 'text-yellow-500' : 'text-gray-300'}`}
                                aria-label={`rate-${val}`}
                              >
                                <Star className={`h-6 w-6 ${reviewForm.rating >= val ? 'fill-current' : ''}`} />
                              </button>
                            );
                          })}
                          <span className="text-sm text-gray-500 ml-2">({reviewForm.rating || 0}/5)</span>
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-700">{t('your_review')}</Label>
                        <Textarea
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          className="mt-2"
                          rows={3}
                          placeholder={t('your_review')}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={reviewSubmitting}
                        className="bg-yellow-600 hover:bg-yellow-500"
                      >
                        {reviewSubmitting ? t('loading') : t('submit_review')}
                      </Button>
                    </form>
                  </div>
                </div>


            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h1 data-testid="car-details-title" className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {car.brand} {car.model}
                    </h1>
                    <p className="text-gray-500">{car.year}</p>
                  </div>
                  <span className={`badge ${car.available ? 'badge-available' : 'badge-unavailable'}`}>
                    {car.available ? t('available') : t('not_available')}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Fuel className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('fuel')}</p>
                      <p className="font-semibold">{car.fuel_type}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Settings className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('transmission')}</p>
                      <p className="font-semibold">{car.transmission}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('seats')}</p>
                      <p className="font-semibold">{car.seats} {t('persons')}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-purple-600">€</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('price_per_day')}</p>
                      <p className="font-semibold">€{car.price_per_day}{t('per_day')}</p>
                    </div>
                  </div>
                </div>

                {car.description && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-bold text-lg mb-3">{t('description')}</h3>
                    <p className="text-gray-600 leading-relaxed">{car.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking Form */}
          <div>
            <Card className="border-none shadow-xl sticky top-6">
              <CardContent className="p-8">
                <h2 data-testid="booking-form-title" className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {t('booking_title')}
                </h2>

                {/* Period Pricing Table */}
                {priceDetails?.periods && priceDetails.periods.length > 0 && (
                  <div className="border-t border-gray-200 pt-6 mt-6 mb-6">
                    <h3 className="font-bold text-lg mb-3">{t('period_prices_title') || 'Çmimet sipas periudhave'}</h3>
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-4 py-3 text-gray-600">{t('period_name') || 'Periudha'}</th>
                            <th className="text-left px-4 py-3 text-gray-600">{t('period_range') || 'Data'}</th>
                            <th className="text-right px-4 py-3 text-gray-600">{t('price_per_day') || 'Çmimi/ditë'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {priceDetails.periods.map((p, idx) => (
                            <tr key={idx} className="border-t border-gray-200 bg-white">
                              <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
                              <td className="px-4 py-3 text-gray-600">{p.start} - {p.end}</td>
                              <td className="px-4 py-3 text-right font-semibold text-gray-900">€{Number(p.price_per_day || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('period_prices_note') || 'Sezoni Veror: 1 Qershor - 30 Shtator. Sezoni Dimëror: 1 Tetor - 30 Maj.'}
                    </p>
                  </div>
                )}

                <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                  {/* Visual Calendar with Booked Dates */}
                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <CalendarIcon className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-800">{t('select_dates')}</h4>
                    </div>
                    
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mb-4 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-500 rounded"></div>
                        <span className="text-gray-600">{t('legend_confirmed')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-orange-400 rounded"></div>
                        <span className="text-gray-600">{t('legend_pending')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded"></div>
                        <span className="text-gray-600">{t('legend_selected')}</span>
                      </div>
                    </div>

                    {/* Interactive Calendar */}
                    <div className="flex justify-center">
                      <Calendar
                        mode="range"
                        selected={{
                          from: pickupDate ? new Date(pickupDate) : undefined,
                          to: returnDate ? new Date(returnDate) : undefined
                        }}
                        onSelect={(range) => {
                          if (range?.from) {
                            setPickupDate(formatLocalDateYYYYMMDD(range.from));
                          }
                          if (range?.to) {
                            setReturnDate(formatLocalDateYYYYMMDD(range.to));
                          } else if (range?.from && !range?.to) {
                            setReturnDate("");
                          }
                        }}
                        disabled={(date) => {
                          if (date < new Date().setHours(0, 0, 0, 0)) return true;
                          return false;
                        }}
                        modifiers={{
                          booked_confirmed: bookedDates
                            .filter(b => b.status === 'confirmed')
                            .flatMap(range => {
                              const dates = [];
                              const start = new Date(range.pickup_date + 'T00:00:00');
                              const end = new Date(range.return_date + 'T00:00:00');
                              for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                                dates.push(new Date(d.getTime()));
                              }
                              return dates;
                            }),
                          booked_pending: bookedDates
                            .filter(b => b.status === 'pending')
                            .flatMap(range => {
                              const dates = [];
                              const start = new Date(range.pickup_date + 'T00:00:00');
                              const end = new Date(range.return_date + 'T00:00:00');
                              for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                                dates.push(new Date(d.getTime()));
                              }
                              return dates;
                            })
                        }}
                        modifiersClassNames={{
                          booked_confirmed: 'rdp-day_booked_confirmed',
                          booked_pending: 'rdp-day_booked_pending'
                        }}
                        numberOfMonths={1}
                        className="rounded-lg border shadow-sm bg-white"
                      />
                    </div>

                    {/* Selected Dates Display */}
                    {(pickupDate || returnDate) && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800 font-medium">{t('selected_dates')}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div>
                            <span className="text-xs text-gray-500">{t('pickup')}:</span>
                            <p className="font-semibold text-blue-700">
                              {pickupDate ? new Date(pickupDate).toLocaleDateString('sq-AL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                          <div>
                            <span className="text-xs text-gray-500">{t('return_label')}:</span>
                            <p className="font-semibold text-blue-700">
                              {returnDate ? new Date(returnDate).toLocaleDateString('sq-AL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Same-day booking indicator */}
                        {isSameDay && (
                          <div className="mt-2 px-2 py-1 bg-purple-100 rounded text-purple-700 text-xs inline-block">
                            🕐 {t('same_day_booking')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Time Selection */}
                  {(pickupDate && returnDate) && (
                    <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold text-gray-800">{t('pickup_time')} & {t('return_time')}</h4>
                      </div>
                      
                      {/* 24/7 Service Note */}
                      <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 flex items-center gap-2">
                          <span className="text-lg">🌙</span>
                          {t('time_24_7')}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Pickup Time */}
                        <div>
                          <Label className="text-gray-600 text-sm">{t('pickup_time')}</Label>
                          <select
                            value={pickupTime}
                            onChange={(e) => setPickupTime(e.target.value)}
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                          >
                            {TIME_OPTIONS.map((time) => (
                              <option key={`pickup-${time}`} value={time}>{time}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Return Time */}
                        <div>
                          <Label className="text-gray-600 text-sm">{t('return_time')}</Label>
                          <select
                            value={returnTime}
                            onChange={(e) => setReturnTime(e.target.value)}
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                          >
                            {TIME_OPTIONS.map((time) => (
                              <option key={`return-${time}`} value={time}>{time}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Same-day booking note */}
                      {isSameDay && (
                        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-700">
                            ℹ️ {t('same_day_note')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Booked Dates List (Collapsible) */}
                  {bookedDates.length > 0 && (
                    <details className="bg-amber-50 border border-amber-200 rounded-lg">
                      <summary className="p-3 cursor-pointer flex items-center gap-2 text-amber-800 font-medium">
                        <CalendarIcon className="h-4 w-4" />
                        {t('view_existing_bookings').replace('{count}', bookedDates.length)}
                      </summary>
                      <div className="p-3 pt-0 space-y-2">
                        {bookedDates.map((range, index) => (
                          <div key={index} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm">
                            <div className="text-gray-700">
                              <span>{new Date(range.pickup_date).toLocaleDateString('sq-AL')}</span>
                              {range.pickup_time && <span className="text-purple-600 ml-1">({range.pickup_time})</span>}
                              <span> - </span>
                              <span>{new Date(range.return_date).toLocaleDateString('sq-AL')}</span>
                              {range.return_time && <span className="text-purple-600 ml-1">({range.return_time})</span>}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              range.status === 'confirmed' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {range.status === 'confirmed' ? t('confirmed') : t('pending')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Date Conflict Warning */}
                  {dateConflict && dateConflict.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <h4 className="font-semibold text-red-800">{t('car_not_available')}</h4>
                      </div>
                      <p className="text-sm text-red-700 mb-2">
                        {t('dates_conflict')}
                      </p>
                      <ul className="text-sm text-red-600 list-disc list-inside">
                        {dateConflict.map((conflict, index) => (
                          <li key={index}>
                            {new Date(conflict.pickup_date).toLocaleDateString('sq-AL')} - {new Date(conflict.return_date).toLocaleDateString('sq-AL')}
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-red-700 mt-2 font-medium">
                        {t('select_other_dates')}
                      </p>
                    </div>
                  )}

                  {/* Availability Check Loading */}
                  {checkingAvailability && (
                    <div className="flex items-center gap-2 text-blue-600">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-r-transparent"></div>
                      <span className="text-sm">{t('checking_availability')}</span>
                    </div>
                  )}

                  {/* Price Summary */}
                  {totalDays > 0 && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">{t('num_days')}:</span>
                        <span className="font-semibold">{totalDays}</span>
                      </div>
                      
                      {priceDetails && priceDetails.discount_percent > 0 ? (
                        <>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-gray-600">{t('base_price')}:</span>
                            <span className="text-gray-500 line-through">€{priceDetails.base_price.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-green-600 font-medium">
                              🎉 {t('discount')} {priceDetails.discount_percent}%
                            </span>
                            <span className="text-green-600 font-medium">-€{priceDetails.discount_amount.toFixed(2)}</span>
                          </div>
                          <div className="border-t border-blue-200 pt-2 mt-2">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-900 font-bold text-lg">{t('total')}:</span>
                              <span className="text-green-600 font-bold text-2xl">€{priceDetails.final_price.toFixed(2)}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-900 font-bold text-lg">{t('total')}:</span>
                          <span className="text-blue-600 font-bold text-2xl">€{totalPrice}</span>
                        </div>
                      )}
                      
                      {/* Show available discounts */}
                      {discounts.length > 0 && !priceDetails?.discount_percent && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs text-gray-500 mb-1">💡 {t('available_discounts')}</p>
                          <div className="flex flex-wrap gap-2">
                            {discounts.map((d, idx) => (
                              <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                {d.description}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Customer Info */}
                  <div>
                    <Label htmlFor="booking-name">{t('full_name')}</Label>
                    <Input
                      id="booking-name"
                      data-testid="booking-name-input"
                      value={bookingForm.customer_name}
                      onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="booking-email">{t('email')}</Label>
                    <Input
                      id="booking-email"
                      data-testid="booking-email-input"
                      type="email"
                      value={bookingForm.customer_email}
                      onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="booking-phone">{t('phone')}</Label>
                    <Input
                      id="booking-phone"
                      data-testid="booking-phone-input"
                      value={bookingForm.customer_phone}
                      onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})}
                      required
                      className="mt-2"
                    />
                  </div>


                    {/* Insurance Selection */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="font-semibold text-gray-900 mb-3">{t('insurance_title') || 'Siguracion'}</p>
                      <div className="space-y-2">
                        <label className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-md cursor-pointer">
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="insurance"
                              checked={insuranceType === 'TPL'}
                              onChange={() => {
                                setInsuranceType('TPL');
                                setInsurancePricePerDay(Number(car?.tpl_price_per_day || 0));
                              }}
                            />
                            <span className="text-gray-900">{t('insurance_tpl') || 'TPL'}</span>
                          </div>
                          <span className="text-gray-700 font-semibold">€{Number(car?.tpl_price_per_day || 0).toFixed(2)}{t('per_day')}</span>
                        </label>

                        <label className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-md cursor-pointer">
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="insurance"
                              checked={insuranceType === 'mini_kasko'}
                              onChange={() => {
                                setInsuranceType('mini_kasko');
                                setInsurancePricePerDay(Number(car?.mini_kasko_price_per_day || 0));
                              }}
                            />
                            <span className="text-gray-900">{t('insurance_mini_kasko') || 'Mini Kasko'}</span>
                          </div>
                          <span className="text-gray-700 font-semibold">€{Number(car?.mini_kasko_price_per_day || 0).toFixed(2)}{t('per_day')}</span>
                        </label>

                        <label className="flex items-center justify-between gap-3 p-3 bg-white border border-gray-200 rounded-md cursor-pointer">
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="insurance"
                              checked={insuranceType === 'full_kasko'}
                              onChange={() => {
                                setInsuranceType('full_kasko');
                                setInsurancePricePerDay(Number(car?.full_kasko_price_per_day || 0));
                              }}
                            />
                            <span className="text-gray-900">{t('insurance_full_kasko') || 'Full Kasko'}</span>
                          </div>
                          <span className="text-gray-700 font-semibold">€{Number(car?.full_kasko_price_per_day || 0).toFixed(2)}{t('per_day')}</span>
                        </label>
                      </div>

                      <p className="text-xs text-gray-500 mt-3">
                        {t('insurance_note') || 'Siguracionet llogariten veç dhe nuk përfshihen në ulje.'}
                      </p>
                    </div>

                  {/* Document Upload Section */}
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Upload className="h-5 w-5 text-red-600" />
                      <h4 className="font-semibold text-gray-800">{t('documents_required')} *</h4>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">{t('upload_instructions')}</p>
                    
                    {/* ID Document Upload */}
                    <div className="mb-4">
                      <Label className="text-gray-700 text-sm font-medium">{t('id_document')}</Label>
                      <input
                        type="file"
                        ref={idInputRef}
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => handleDocumentUpload(e.target.files[0], 'id')}
                        className="hidden"
                      />
                      {idDocument ? (
                        <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <FileCheck className="h-5 w-5 text-green-600" />
                            <span className="text-sm text-green-700 truncate max-w-[200px]">{idDocument.originalName}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setIdDocument(null)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => idInputRef.current?.click()}
                          disabled={idUploading}
                          variant="outline"
                          className="mt-2 w-full border-dashed border-2 border-gray-300 hover:border-red-400 hover:bg-red-50"
                        >
                          {idUploading ? (
                            <><div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-r-transparent mr-2"></div>{t('uploading')}</>
                          ) : (
                            <><Upload className="h-4 w-4 mr-2" />{t('upload_id')}</>
                          )}
                        </Button>
                      )}
                    </div>
                    
                    {/* License Document Upload */}
                    <div>
                      <Label className="text-gray-700 text-sm font-medium">{t('license_document')}</Label>
                      <input
                        type="file"
                        ref={licenseInputRef}
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => handleDocumentUpload(e.target.files[0], 'license')}
                        className="hidden"
                      />
                      {licenseDocument ? (
                        <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <FileCheck className="h-5 w-5 text-green-600" />
                            <span className="text-sm text-green-700 truncate max-w-[200px]">{licenseDocument.originalName}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setLicenseDocument(null)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => licenseInputRef.current?.click()}
                          disabled={licenseUploading}
                          variant="outline"
                          className="mt-2 w-full border-dashed border-2 border-gray-300 hover:border-red-400 hover:bg-red-50"
                        >
                          {licenseUploading ? (
                            <><div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-r-transparent mr-2"></div>{t('uploading')}</>
                          ) : (
                            <><Upload className="h-4 w-4 mr-2" />{t('upload_license')}</>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="booking-message">{t('message_optional')}</Label>
                    <Textarea
                      id="booking-message"
                      data-testid="booking-message-textarea"
                      value={bookingForm.message}
                      onChange={(e) => setBookingForm({...bookingForm, message: e.target.value})}
                      rows={3}
                      className="mt-2"
                      placeholder={t('additional_notes')}
                    />
                  </div>

                  {/* Payment Options */}
                  <div className="space-y-3">
                    {/* Pay at Airport Button */}
                    <Button 
                      data-testid="submit-booking-button"
                      onClick={handleBookingWithoutPayment}
                      type="button"
                      className={`w-full py-6 text-lg font-semibold border shadow-lg luxury-text tracking-wider uppercase ${
                        dateConflict && dateConflict.length > 0 || !idDocument || !licenseDocument
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed border-gray-400'
                          : 'bg-gradient-to-r from-yellow-700 to-yellow-600 hover:from-yellow-600 hover:to-yellow-500 text-white border-yellow-600/30 shadow-yellow-900/30'
                      }`}
                      disabled={!car.available || (dateConflict && dateConflict.length > 0) || checkingAvailability || !idDocument || !licenseDocument}
                    >
                      {dateConflict && dateConflict.length > 0 ? (
                        <>
                          <AlertTriangle className="mr-2 h-5 w-5" />
                          {t('dates_taken')}
                        </>
                      ) : (
                        <>
                          <Check className="mr-2 h-5 w-5" />
                          {car.available ? t('pay_at_airport') : t('not_available')}
                        </>
                      )}
                    </Button>

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">{t('or')}</span>
                      </div>
                    </div>

                    {/* Pay Online with Stripe Button */}
                    <Button 
                      data-testid="pay-online-button"
                      onClick={handlePayOnline}
                      type="button"
                      className={`w-full py-6 text-lg font-semibold border shadow-lg tracking-wider uppercase ${
                        dateConflict && dateConflict.length > 0 || !idDocument || !licenseDocument || paymentLoading
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed border-gray-400'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white border-blue-600/30 shadow-blue-900/30'
                      }`}
                      disabled={!car.available || (dateConflict && dateConflict.length > 0) || checkingAvailability || !idDocument || !licenseDocument || paymentLoading}
                    >
                      {paymentLoading ? (
                        <>
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-r-transparent mr-2"></div>
                          {t('processing')}
                        </>
                      ) : (
                        <>
                          <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 10h18v7c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2v-7zm0-3c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v1H3V7z"/>
                          </svg>
                          {t('pay_online')}
                        </>
                      )}
                    </Button>
                    
                    {/* Stripe Security Badge */}
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                      </svg>
                      {t('secure_payment')}
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Image Modal with Gallery */}
      {showImageModal && (
        <div 
          className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-7xl max-h-screen" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-yellow-600 transition-colors z-10"
            >
              <X className="h-10 w-10" />
            </button>
            
            <div className="bg-zinc-500 rounded-lg shadow-2xl overflow-hidden p-4 relative">
              <img 
                src={allImages[currentImageIndex] ? (allImages[currentImageIndex].startsWith('http') ? allImages[currentImageIndex] : `${BACKEND_URL}${allImages[currentImageIndex]}`) : car.image_url}
                alt={`${car.brand} ${car.model}`}
                className="max-w-full max-h-[80vh] object-contain mx-auto"
              />
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : allImages.length - 1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-3 transition-colors"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev < allImages.length - 1 ? prev + 1 : 0)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-3 transition-colors"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 rounded-full px-4 py-1">
                    <span className="text-white text-sm">{currentImageIndex + 1} / {allImages.length}</span>
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnails in modal */}
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-3 justify-center overflow-x-auto pb-2">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex ? 'border-yellow-500' : 'border-transparent hover:border-gray-400'
                    }`}
                  >
                    <img src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetailsPage;
